import type { ComponentKind, ComponentType, Tracking } from '../../../data'
import type { Category } from '../../../data/inventory'
import type { PreparedImage } from '../../imageFile'
import { TreeIcon } from '../../tree/treeIcons'
import { Glyph } from '../glyphs'
import { NEW_CATEGORY, shapeOf } from '../itemDraft'
import type { Draft, Problems } from '../itemDraft'
import { EditorPhoto } from './EditorPhoto'
import { EditorSection, Field, OptionCards } from './controls'
import type { Option } from './controls'

const KINDS: readonly Option<ComponentKind>[] = [
  { value: 'buy', title: 'Bought', note: 'Someone else makes it; we order it in.', icon: <TreeIcon name="bought" size={20} /> },
  { value: 'make_external', title: 'Made by a vendor', note: 'Our design, machined or printed for us.', icon: <TreeIcon name="vendor" size={20} /> },
  { value: 'make_internal', title: 'Built here', note: 'Assembled on our bench from other items.', icon: <TreeIcon name="built" size={20} /> },
]

const COUNTING: readonly Option<Tracking>[] = [
  {
    value: 'serialized',
    title: 'Each one has its own serial',
    note: 'Cameras, actuators, arms — each one’s history is kept.',
    icon: <Glyph name="tag" size={20} />,
  },
  {
    value: 'quantity',
    title: 'Counted in bulk',
    note: 'Screws, cable, glue — only how many are left matters.',
    icon: <Glyph name="fastener" size={20} />,
  },
]

/** Why kind and counting cannot change any more — `reshapable` is the server's answer; this only says it. */
function lockReason(t: ComponentType): string {
  const why =
    t.instanceCount > 0
      ? `${t.instanceCount} of these ${t.instanceCount === 1 ? 'has' : 'have'} already been received or built`
      : t.revisionCount > 0
        ? 'it already has a parts list'
        : 'another item’s parts list uses it'
  return `Fixed now — ${why}. To change these, make a new item.`
}

export function BasicsSection({
  draft,
  set,
  existing,
  problems,
  categories,
  imageId,
  staged,
  onStage,
}: {
  draft: Draft
  set: (patch: Partial<Draft>) => void
  existing: ComponentType | undefined
  problems: Problems
  categories: readonly Category[]
  imageId: string | null
  staged: PreparedImage | null
  onStage: (image: PreparedImage | null) => void
}): JSX.Element {
  const shape = shapeOf(draft)
  const locked = existing !== undefined && !existing.reshapable
  const listed = categories.some((c) => c.name === draft.category)
  const footnote = draft.isRoot ? 'A whole robot is built here and has its own serial — set under Tracking details.' : locked && existing ? lockReason(existing) : null

  return (
    <EditorSection title="Basics" intro="What it is, and how we get and count it.">
      <div className="ie-basics">
        <EditorPhoto
          itemId={existing?.id ?? null}
          imageId={imageId}
          name={draft.name}
          category={draft.category}
          staged={staged}
          onStage={onStage}
        />
        <div className="ie-basics__fields">
          <Field label="Name" error={problems.name} note="What people call it out loud.">
            <input value={draft.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Shoulder actuator" data-autofocus maxLength={120} />
          </Field>
          <Field label="Category" note="Groups it in Inventory. Nothing else reads it.">
            <select value={draft.category} onChange={(e) => set({ category: e.target.value })}>
              <option value="">No category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
              {/* An item filed under a name the list no longer has still shows it, rather than silently losing it. */}
              {draft.category !== '' && draft.category !== NEW_CATEGORY && !listed ? <option value={draft.category}>{draft.category}</option> : null}
              <option value={NEW_CATEGORY}>New category…</option>
            </select>
          </Field>
          {draft.category === NEW_CATEGORY ? (
            <Field label="New category name" error={problems.newCategory}>
              <input value={draft.newCategory} onChange={(e) => set({ newCategory: e.target.value })} maxLength={40} autoFocus />
            </Field>
          ) : null}
        </div>
      </div>

      <OptionCards
        legend="How does it come to exist?"
        value={shape.kind}
        options={KINDS}
        onChange={(kind) => set({ kind })}
        disabled={locked || draft.isRoot}
      />
      <OptionCards
        legend="How is it counted?"
        value={shape.tracking}
        options={COUNTING}
        onChange={(tracking) => set({ tracking })}
        disabled={locked || draft.isRoot}
        footnote={footnote}
      />
      {shape.tracking === 'quantity' ? (
        <div className="ie-row">
          <Field label="Counted in" note="pcs, m, g, ml — whatever you order and use it by.">
            <input value={draft.unit} onChange={(e) => set({ unit: e.target.value })} placeholder="pcs" maxLength={16} />
          </Field>
        </div>
      ) : null}
    </EditorSection>
  )
}
